import { useCallback, useMemo } from 'react';
import { Flex } from '@banhala/ui-kit';
import { type NavigationProp, useNavigation } from '@react-navigation/native';
import { MultiCheckboxRow } from './MultiCheckboxRow';
import type {
  MultiCheckboxFilterProps,
  MultiCheckboxFilterEntity,
  MultiCheckboxMoreCta,
  MultiCheckboxRow as MultiCheckboxRowType,
} from './types';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';
import { useFilterListAtom } from '../FilterList/Provider/filterList/useFilterListAtom';
import { useLogClickFilter } from '../FilterList/hooks/useLogClickFilter';
import { useFilterBottomSheet } from '../FilterList/components/useFilterBottomSheet';
import { bridge } from '../../../../utils';

export const MultiCheckboxFilter = ({
  body,
  entity,
  logging,
  scrollToFilterList,
}: MultiCheckboxFilterProps) => {
  const navigation = useNavigation<
    NavigationProp<{
      [route: string]: Record<string, string[] | undefined>;
    }>
  >();
  const { setFilterListAtom } = useFilterListAtom();
  const logClickFilter = useLogClickFilter(logging);
  const { show: showFilterBottomSheet } = useFilterBottomSheet();

  const rows = useMemo(
    () => entity?.map((e: MultiCheckboxFilterEntity) => e.item) ?? [],
    [entity],
  );

  // 각 queryName별 선택값 Set 생성
  const appliedValuesMap = useMemo(() => {
    const appliedQuery = body?.applied_query ?? {};
    const map: Record<string, Set<string>> = {};
    for (const row of rows) {
      const raw = appliedQuery[row.query_name];
      if (raw == null) {
        map[row.query_name] = new Set();
        continue;
      }
      if (Array.isArray(raw)) {
        map[row.query_name] = new Set(raw);
      } else if (typeof raw === 'string') {
        // 쉼표 구분 문자열 지원
        map[row.query_name] = new Set(raw.split(',').filter(Boolean));
      } else {
        map[row.query_name] = new Set();
      }
    }
    return map;
  }, [rows, body]);

  const handleToggleItem = useCallback(
    (
      queryName: string,
      queryValue: string,
      itemText: string,
      itemLogging?: IScreensLogging,
    ) => {
      const currentValues = appliedValuesMap[queryName] ?? new Set();
      const isSelected = currentValues.has(queryValue);

      const newValues = new Set(currentValues);
      if (isSelected) {
        newValues.delete(queryValue);
      } else {
        newValues.add(queryValue);
      }

      const nextValue = newValues.size > 0 ? Array.from(newValues) : undefined;

      // iOS: SELECTED_ITEM = item.text, logging = 그룹 + 아이템 머지
      logClickFilter({ selectedItem: itemText, itemLogging });
      scrollToFilterList();
      setFilterListAtom(prev => ({
        ...prev,
        [queryName]: nextValue,
      }));
      navigation.setParams({ [queryName]: nextValue });
    },
    [
      appliedValuesMap,
      logClickFilter,
      scrollToFilterList,
      setFilterListAtom,
      navigation,
    ],
  );

  const handlePressMoreCta = useCallback(
    (moreCta: MultiCheckboxMoreCta) => {
      // iOS: CLICK_FILTER_MORE 별도 이벤트, moreCta.logging만 사용
      bridge.logAnalyticsEvent({
        name: 'CLICK_FILTER_MORE',
        ...moreCta.logging,
      });

      if (moreCta.deeplink) {
        bridge.openDeeplink(moreCta.deeplink);
        return;
      }

      if (moreCta.filter_token) {
        showFilterBottomSheet({
          filterToken: moreCta.filter_token,
          scrollToFilterList,
        });
      }
    },
    [showFilterBottomSheet, scrollToFilterList],
  );

  if (rows.length === 0) {
    return null;
  }

  return (
    <Flex backgroundColor="bg_secondary" padding={{ vertical: 6 }}>
      {rows.map((row: MultiCheckboxRowType) => (
        <MultiCheckboxRow
          key={row.query_name}
          row={row}
          appliedValues={appliedValuesMap[row.query_name] ?? EMPTY_SET}
          onToggleItem={handleToggleItem}
          onPressMoreCta={handlePressMoreCta}
        />
      ))}
    </Flex>
  );
};

const EMPTY_SET = new Set<string>();
